import { Tour, TourProps } from 'antd'
import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { NotificationContext } from 'src/context/NotificationContext'
import { useHistory } from 'react-router-dom'
import { AuthContext } from 'src/components/Auth/AuthProvider'
import { getRoleMapping } from 'src/constants/role.constant'
import { Role } from 'src/gql/graphql'
interface CaiTourProps {
  open: boolean
  setOpen: (open: boolean) => void
  initialFeedback?: string
}

async function mp3ToBlob(mp3Url: string) {
  const response = await fetch(mp3Url)
  const arrayBuffer = await response.arrayBuffer()
  const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' })
  return blob
}

function getMp3Links(lang: string) {
  return [
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-1.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-2.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-3.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-4.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-5.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-6.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-7.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-8.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-9.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-10.mp3`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/${lang}/HR_WT-042025-11.mp3`,
  ]
}

const getTourImgs = () => {
  return [
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-1.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-2.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-3.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-4.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-5.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-6.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-7.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-8.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-9.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-10.png`,
    `${process.env.REACT_APP_ASSETS_ENDPOINT}/powerful-onboarding/imgs/HR_WT-042025-11.png`,
  ]
}

const PowerfulTour: FC<CaiTourProps> = ({ open, setOpen }) => {
  const { t, i18n } = useTranslation()

  const [step, setStep] = useState<number | null>(0)
  const [tourAudio, setTourAudio] = useState<HTMLAudioElement | null>(null)
  const {
    invitationRef,
    employeeDetailsBtnRef,
    employeeDetailsRef,
    programCreateBtnRef,
    firstProgramTileRef,
    firstProgressTrackerRef,
    createTaskBtnRef,
    taskModalRef,
    firstTaskDetailsBtnRef,
    teamRef,
    libraryRef,
  } = useContext(NotificationContext)
  const { userInfo } = useContext(AuthContext)
  const history = useHistory()

  const currentRole = userInfo?.employeeDetails?.role?.name
  const roleMapping = getRoleMapping(userInfo?.organization?.category)

  const language = i18n.language || 'en'
  const mp3Links = getMp3Links(language)
  const tourImgs = getTourImgs()

  const [audioEnabled, setAudioEnabled] = useState(true)
  const [offset, setOffset] = useState<[number, number]>([1, 1])
  const [tourOpen, setTourOpen] = useState(open)

  function waitForRefAndSetStep(
    ref: React.RefObject<HTMLElement> | null,
    step: number,
    offset: [number, number],
  ) {
    if (ref?.current) {
      setStep(step)
      setOffset(offset)
      playAudio(mp3Links[step])
    } else {
      setStep(null)
      setTimeout(() => waitForRefAndSetStep(ref, step, offset), 100)
    }
  }

  const playAudio = useCallback(
    (mp3Url: string) => {
      if (isMobile) return

      if (tourAudio) {
        tourAudio.pause()
        setTourAudio(null)
      }

      mp3ToBlob(mp3Url).then((blob) => {
        const objectURL = URL.createObjectURL(blob)
        const tempAudio = new Audio(objectURL)

        setTourAudio(tempAudio)
        if (audioEnabled) tempAudio.play()
      })
    },
    [audioEnabled, tourAudio],
  )

  const steps: TourProps['steps'] = [
    {
      title: t('caiPopup.tour.step1.title', 'Before you begin'),
      description: t('caiPopup.pTour.step0.description'),
      placement: 'center',
    },
    {
      title: t('caiPopup.pTour.step1.title'),
      description: t('caiPopup.pTour.step1.description'),
      placement: 'bottom',
      target: () => invitationRef?.current as HTMLElement,
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step2.title', {
        name: roleMapping[Role.Employee].toLowerCase(),
      }),
      description: t('caiPopup.pTour.step2.description', {
        name: roleMapping[Role.Employee].toLowerCase(),
      }),
      placement: 'top',
      target: () => employeeDetailsBtnRef?.current as HTMLElement,
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step3.title'),
      description: t('caiPopup.pTour.step3.description'),
      placement: 'right',
      target: () => employeeDetailsRef as HTMLElement,
      onNext: () => {
        history.push(`/${currentRole?.toLowerCase()}-programs`)
        waitForRefAndSetStep(programCreateBtnRef, 4, [1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step4.title'),
      description: t('caiPopup.pTour.step4.description'),
      placement: 'right',
      target: () => programCreateBtnRef?.current as HTMLElement,
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step5.title'),
      description: t('caiPopup.pTour.step5.description'),
      placement: 'right',
      target: () => firstProgramTileRef?.current as HTMLElement,
      onNext: () => {
        history.push(`/${currentRole?.toLowerCase()}-progress-tracker`)
        waitForRefAndSetStep(firstProgressTrackerRef, 6, [1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step6.title'),
      description: t('caiPopup.pTour.step6.description'),
      placement: 'right',
      target: () => firstProgressTrackerRef?.current as HTMLElement,
      onNext: () => {
        history.push(`/${currentRole?.toLowerCase()}-tasks`)
        waitForRefAndSetStep(firstProgressTrackerRef, 7, [1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step7.title'),
      description: t('caiPopup.pTour.step7.description'),
      placement: 'right',
      target: () => createTaskBtnRef?.current as HTMLElement,
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step8.title'),
      description: t('caiPopup.pTour.step8.description'),
      placement: 'right',
      target: () => taskModalRef as HTMLElement,
      onNext: () => {
        const escEvent = new KeyboardEvent('keydown', {
          key: 'Escape',
          code: 'Escape',
          keyCode: 27,
          which: 27,
          bubbles: true,
        })

        document.dispatchEvent(escEvent)
        setStep(9)
        playAudio(mp3Links[9])
        setOffset([1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step9.title'),
      description: t('caiPopup.pTour.step9.description'),
      placement: 'right',
      target: () => firstTaskDetailsBtnRef as HTMLDivElement,
      onNext: () => {
        history.push(`/${currentRole?.toLowerCase()}-teams`)
        waitForRefAndSetStep(teamRef, 10, [1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step10.title'),
      description: t('caiPopup.pTour.step10.description'),
      placement: 'top',
      target: () => teamRef?.current as HTMLElement,
      onNext: () => {
        history.push(`/${currentRole?.toLowerCase()}-library`)
        waitForRefAndSetStep(libraryRef, 11, [1, 1])
      },
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
    {
      title: t('caiPopup.pTour.step11.title'),
      description: t('caiPopup.pTour.step11.description'),
      placement: 'top',
      target: () => libraryRef?.current as HTMLElement,
      prevButtonProps: {
        style: {
          display: 'none',
        },
      },
    },
  ]

  const mobileSteps: TourProps['steps'] = [
    {
      title: t('caiPopup.tour.step1.title', 'Before you begin'),
      description: t('caiPopup.pTour.step0.description'),
      placement: 'center',
    },
    {
      title: t('caiPopup.pTour.step1.title'),
      description: t('caiPopup.pTour.step1.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step1.title')}
          loading="lazy"
          src={tourImgs[0]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step2.title', {
        name: roleMapping[Role.Employee].toLowerCase(),
      }),
      description: t('caiPopup.pTour.step2.description', {
        name: roleMapping[Role.Employee].toLowerCase(),
      }),
      placement: 'center',
      cover: (
        <img
          loading="lazy"
          src={tourImgs[1]}
          alt={t('caiPopup.pTour.step2.title', {
            name: roleMapping[Role.Employee].toLowerCase(),
          })}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step3.title'),
      description: t('caiPopup.pTour.step3.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step3.title')}
          loading="lazy"
          src={tourImgs[2]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step4.title'),
      description: t('caiPopup.pTour.step4.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step4.title')}
          loading="lazy"
          src={tourImgs[3]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step5.title'),
      description: t('caiPopup.pTour.step5.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step5.title')}
          loading="lazy"
          src={tourImgs[4]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step6.title'),
      description: t('caiPopup.pTour.step6.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step6.title')}
          loading="lazy"
          src={tourImgs[5]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step7.title'),
      description: t('caiPopup.pTour.step7.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step7.title')}
          loading="lazy"
          src={tourImgs[6]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step8.title'),
      description: t('caiPopup.pTour.step8.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step8.title')}
          loading="lazy"
          src={tourImgs[7]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step9.title'),
      description: t('caiPopup.pTour.step9.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step9.title')}
          loading="lazy"
          src={tourImgs[8]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step10.title'),
      description: t('caiPopup.pTour.step10.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step10.title')}
          loading="lazy"
          src={tourImgs[9]}
        />
      ),
    },
    {
      title: t('caiPopup.pTour.step11.title'),
      description: t('caiPopup.pTour.step11.description'),
      placement: 'center',
      cover: (
        <img
          alt={t('caiPopup.pTour.step11.title')}
          loading="lazy"
          src={tourImgs[10]}
        />
      ),
    },
  ]

  useEffect(() => {
    if (open) {
      setStep(0)
      setTourOpen(true)

      playAudio(mp3Links[0])
      if (isMobile) {
        const side = document.getElementById('mainSideMenu')
        if (!side?.classList.contains('open')) return
        side.classList.remove('open')
      }
      history.push(`/${currentRole?.toLowerCase()}-dashboard`)
    }
  }, [open])

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      {tourOpen && step !== null && (
        <Tour
          closable
          current={step}
          disabledInteraction={true}
          open={tourOpen}
          rootClassName="mobile-ant-tour"
          steps={isMobile ? mobileSteps : steps}
          zIndex={9999999}
          gap={{
            offset,
          }}
          indicatorsRender={() => (
            <>
              {!isMobile && (
                <i
                  style={{ color: 'var(--icon-color)', cursor: 'pointer' }}
                  className={`${
                    audioEnabled ? 'icofont-volume-down' : 'icofont-volume-mute'
                  } fs-5`}
                  onClick={() => {
                    setAudioEnabled((prev) => {
                      return !prev
                    })
                    if (audioEnabled) {
                      tourAudio?.pause()
                    } else {
                      tourAudio?.play()
                    }
                  }}
                ></i>
              )}
            </>
          )}
          onChange={(e) => {
            setStep(e)
            if (isMobile) return
            switch (e) {
              case 2:
                setOffset([1, 1])
                break
              case 3:
                employeeDetailsBtnRef?.current?.click()
                setOffset([30, 270])
                break

              case 4:
                setOffset([1, 1])
                break
              case 8:
                createTaskBtnRef?.current?.click()
                setOffset([50, 250])
                break
              case 9:
                setOffset([1, 1])
                break
              default:
                break
            }
            playAudio(mp3Links[e])
          }}
          onClose={() => {
            handleClose()
            setTourOpen(false)
            setStep(null)
          }}
          onFinish={() => {
            handleClose()
            setTourOpen(false)
            setStep(null)
          }}
        />
      )}
    </>
  )
}

export default PowerfulTour
